import { useState, useCallback } from 'react';
import {
  Customer,
  CustomerCreateParams,
  Subscription,
  SubscriptionUpdateParams,
  SubscriptionStatus,
  CheckoutParams,
  CheckoutSession,
} from '../../shared/types';
import { PaymentsConfig } from '../../shared/types/config';
import { PaymentsError, PaymentErrorType } from '../../shared/types/errors';
import { ERROR_MESSAGES } from '../../shared/constants';

/**
 * Native-specific usePayments hook
 * This hook is designed for React Native applications and makes server-side API calls
 * instead of directly using the Node.js Stripe SDK
 */

interface UsePaymentsStore {
  config: PaymentsConfig | null;
  initialized: boolean;
  loading: boolean;
  error: PaymentsError | null;
  // Backward compatibility getters
  isLoading: boolean; // Maps to loading
  isError: boolean; // Maps to !!error
  customer: Customer | null;
  subscriptions: Subscription[];
  activeSubscription: Subscription | null;
  initialize: (config?: PaymentsConfig) => Promise<void>;
  createCustomer: (params: CustomerCreateParams) => Promise<Customer>;
  retrieveCustomer: (customerId: string) => Promise<Customer | null>;
  createCheckoutSession: (params: CheckoutParams) => Promise<CheckoutSession>;
  retrieveCheckoutSession: (sessionId: string) => Promise<CheckoutSession>;
  createSubscription: (params: {
    priceId: string;
    quantity?: number;
  }) => Promise<Subscription>;
  cancelSubscription: (subscriptionId: string) => Promise<void>;
  reactivateSubscription: (subscriptionId: string) => Promise<void>;
  listSubscriptions: (
    customerId: string,
    status?: SubscriptionStatus
  ) => Promise<Subscription[]>;
  retrieveSubscription: (
    subscriptionId: string
  ) => Promise<Subscription | null>;
  updateSubscription: (
    subscriptionId: string,
    params: SubscriptionUpdateParams
  ) => Promise<Subscription>;
  refreshCustomer: (customerId: string) => Promise<void>;
  refreshSubscriptions: (
    customerId: string,
    status?: SubscriptionStatus
  ) => Promise<void>;
  reset: () => void;
}

export const usePayments = (): UsePaymentsStore => {
  const [config, setConfig] = useState<PaymentsConfig | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<PaymentsError | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);

  const handleError = useCallback((err: PaymentsError | Error) => {
    const paymentError =
      err instanceof PaymentsError
        ? err
        : new PaymentsError(PaymentErrorType.UNKNOWN_ERROR, err.message);
    setError(paymentError);
    console.error('Payments error:', paymentError);
  }, []);

  const initialize = useCallback(
    async (initialConfig?: PaymentsConfig) => {
      setLoading(true);
      setError(null);
      try {
        if (!initialConfig) {
          throw new PaymentsError(
            PaymentErrorType.CONFIGURATION_ERROR,
            'Configuration is required for native initialization'
          );
        }

        // For React Native, we just store the config
        // No server-side Stripe SDK initialization needed
        setConfig(initialConfig);
        setInitialized(true);
      } catch (err: unknown) {
        handleError(err as PaymentsError);
      } finally {
        setLoading(false);
      }
    },
    [handleError]
  );

  const refreshSubscriptions = useCallback(
    async (customerId: string, status?: SubscriptionStatus) => {
      if (!initialized) {
        throw new PaymentsError(
          PaymentErrorType.PROVIDER_NOT_CONFIGURED,
          ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED
        );
      }
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `/api/payments/subscriptions?customerId=${customerId}${status ? `&status=${status}` : ''}`,
          {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
          }
        );

        if (!response.ok) {
          throw new PaymentsError(
            PaymentErrorType.NETWORK_ERROR,
            'Failed to fetch subscriptions'
          );
        }

        const fetchedSubscriptions = await response.json();
        setSubscriptions(fetchedSubscriptions);
      } catch (err: unknown) {
        handleError(err as PaymentsError);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [initialized, handleError]
  );

  const retrieveCustomer = useCallback(
    async (customerId: string) => {
      if (!initialized) {
        throw new PaymentsError(
          PaymentErrorType.PROVIDER_NOT_CONFIGURED,
          ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED
        );
      }
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`/api/payments/customers/${customerId}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) {
          throw new PaymentsError(
            PaymentErrorType.NETWORK_ERROR,
            'Failed to retrieve customer'
          );
        }

        const newCustomer = await response.json();
        setCustomer(newCustomer);
        return newCustomer;
      } catch (err: unknown) {
        handleError(err as PaymentsError);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [initialized, handleError]
  );

  const createCustomer = useCallback(
    async (params: CustomerCreateParams) => {
      if (!initialized) {
        throw new PaymentsError(
          PaymentErrorType.PROVIDER_NOT_CONFIGURED,
          ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED
        );
      }
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('/api/payments/customers', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(params),
        });

        if (!response.ok) {
          throw new PaymentsError(
            PaymentErrorType.NETWORK_ERROR,
            'Failed to create customer'
          );
        }

        const newCustomer = await response.json();
        setCustomer(newCustomer);
        return newCustomer;
      } catch (err: unknown) {
        handleError(err as PaymentsError);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [initialized, handleError]
  );

  const createSubscription = useCallback(
    async (params: { priceId: string; quantity?: number }) => {
      if (!initialized || !customer) {
        throw new PaymentsError(
          PaymentErrorType.PROVIDER_NOT_CONFIGURED,
          ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED
        );
      }
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('/api/payments/subscriptions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customerId: customer.id,
            items: [{ priceId: params.priceId, quantity: params.quantity }],
          }),
        });

        if (!response.ok) {
          throw new PaymentsError(
            PaymentErrorType.NETWORK_ERROR,
            'Failed to create subscription'
          );
        }

        const newSubscription = await response.json();
        await refreshSubscriptions(customer.id);
        return newSubscription;
      } catch (err: unknown) {
        handleError(err as PaymentsError);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [initialized, customer, handleError, refreshSubscriptions]
  );

  const listSubscriptions = useCallback(
    async (customerId: string, status?: SubscriptionStatus) => {
      if (!initialized) {
        throw new PaymentsError(
          PaymentErrorType.PROVIDER_NOT_CONFIGURED,
          ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED
        );
      }
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `/api/payments/subscriptions?customerId=${customerId}${status ? `&status=${status}` : ''}`,
          {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
          }
        );

        if (!response.ok) {
          throw new PaymentsError(
            PaymentErrorType.NETWORK_ERROR,
            'Failed to list subscriptions'
          );
        }

        const subs = await response.json();
        setSubscriptions(subs);
        return subs;
      } catch (err: unknown) {
        handleError(err as PaymentsError);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [initialized, handleError]
  );

  const retrieveSubscription = useCallback(
    async (subscriptionId: string) => {
      if (!initialized) {
        throw new PaymentsError(
          PaymentErrorType.PROVIDER_NOT_CONFIGURED,
          ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED
        );
      }
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `/api/payments/subscriptions/${subscriptionId}`,
          {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
          }
        );

        if (!response.ok) {
          throw new PaymentsError(
            PaymentErrorType.NETWORK_ERROR,
            'Failed to retrieve subscription'
          );
        }

        const sub = await response.json();
        if (customer?.id) {
          await refreshSubscriptions(customer.id);
        }
        return sub;
      } catch (err: unknown) {
        handleError(err as PaymentsError);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [initialized, customer, handleError, refreshSubscriptions]
  );

  const createCheckoutSession = useCallback(
    async (params: CheckoutParams) => {
      if (!initialized) {
        throw new PaymentsError(
          PaymentErrorType.PROVIDER_NOT_CONFIGURED,
          ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED
        );
      }
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('/api/payments/checkout-sessions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(params),
        });

        if (!response.ok) {
          throw new PaymentsError(
            PaymentErrorType.NETWORK_ERROR,
            'Failed to create checkout session'
          );
        }

        const session = await response.json();
        return session;
      } catch (err: unknown) {
        handleError(err as PaymentsError);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [initialized, handleError]
  );

  const retrieveCheckoutSession = useCallback(
    async (sessionId: string) => {
      if (!initialized) {
        throw new PaymentsError(
          PaymentErrorType.PROVIDER_NOT_CONFIGURED,
          ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED
        );
      }
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `/api/payments/checkout-sessions/${sessionId}`,
          {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
          }
        );

        if (!response.ok) {
          throw new PaymentsError(
            PaymentErrorType.NETWORK_ERROR,
            'Failed to retrieve checkout session'
          );
        }

        const session = await response.json();
        return session;
      } catch (err: unknown) {
        handleError(err as PaymentsError);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [initialized, handleError]
  );

  const cancelSubscription = useCallback(
    async (subscriptionId: string) => {
      if (!initialized) {
        throw new PaymentsError(
          PaymentErrorType.PROVIDER_NOT_CONFIGURED,
          ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED
        );
      }
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `/api/payments/subscriptions/${subscriptionId}/cancel`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          }
        );

        if (!response.ok) {
          throw new PaymentsError(
            PaymentErrorType.NETWORK_ERROR,
            'Failed to cancel subscription'
          );
        }

        if (customer?.id) {
          await refreshSubscriptions(customer.id);
        }
      } catch (err: unknown) {
        handleError(err as PaymentsError);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [initialized, customer, handleError, refreshSubscriptions]
  );

  const reactivateSubscription = useCallback(
    async (subscriptionId: string) => {
      if (!initialized) {
        throw new PaymentsError(
          PaymentErrorType.PROVIDER_NOT_CONFIGURED,
          ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED
        );
      }
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `/api/payments/subscriptions/${subscriptionId}/reactivate`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          }
        );

        if (!response.ok) {
          throw new PaymentsError(
            PaymentErrorType.NETWORK_ERROR,
            'Failed to reactivate subscription'
          );
        }

        if (customer?.id) {
          await refreshSubscriptions(customer.id);
        }
      } catch (err: unknown) {
        handleError(err as PaymentsError);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [initialized, customer, handleError, refreshSubscriptions]
  );

  const updateSubscription = useCallback(
    async (subscriptionId: string, params: SubscriptionUpdateParams) => {
      if (!initialized) {
        throw new PaymentsError(
          PaymentErrorType.PROVIDER_NOT_CONFIGURED,
          ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED
        );
      }
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `/api/payments/subscriptions/${subscriptionId}`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(params),
          }
        );

        if (!response.ok) {
          throw new PaymentsError(
            PaymentErrorType.NETWORK_ERROR,
            'Failed to update subscription'
          );
        }

        const updatedSubscription = await response.json();
        if (customer?.id) {
          await refreshSubscriptions(customer.id);
        }
        return updatedSubscription;
      } catch (err: unknown) {
        handleError(err as PaymentsError);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [initialized, customer, handleError, refreshSubscriptions]
  );

  const refreshCustomer = useCallback(
    async (customerId: string) => {
      if (!initialized) {
        throw new PaymentsError(
          PaymentErrorType.PROVIDER_NOT_CONFIGURED,
          ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED
        );
      }
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`/api/payments/customers/${customerId}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) {
          throw new PaymentsError(
            PaymentErrorType.NETWORK_ERROR,
            'Failed to refresh customer'
          );
        }

        const fetchedCustomer = await response.json();
        setCustomer(fetchedCustomer);
      } catch (err: unknown) {
        handleError(err as PaymentsError);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [initialized, handleError]
  );

  const reset = useCallback(() => {
    setConfig(null);
    setInitialized(false);
    setLoading(false);
    setError(null);
    setCustomer(null);
    setSubscriptions([]);
  }, []);

  const activeSubscription =
    subscriptions.find(s => s.status === 'active' || s.status === 'trialing') ||
    null;

  return {
    config,
    initialized,
    loading,
    error,
    // Backward compatibility getters
    isLoading: loading,
    isError: !!error,
    customer,
    subscriptions,
    activeSubscription,
    initialize,
    createCustomer,
    retrieveCustomer,
    createCheckoutSession,
    retrieveCheckoutSession,
    createSubscription,
    cancelSubscription,
    reactivateSubscription,
    listSubscriptions,
    retrieveSubscription,
    updateSubscription,
    refreshCustomer,
    refreshSubscriptions,
    reset,
  };
};
